import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, withStyles } from "tss-react/mui";
import { DateTime } from "luxon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Home from "@mui/icons-material/Home";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ActiveScheduleIcon from "./ActiveScheduleIcon";
import DialogWrapperWithClose from "../General/DialogWrapperWithClose";
import MissionSelect from "./MissionSelect";
import NavigatorSelection, { NavigatorChangeProps } from "../Room/Selectors/NavigatorSelection";
import HelpRobotDialog from "./HelpRobotDialog";
import QuickCleanZoneTab from "./QuickCleanZoneTab";
import { TabPanelA11yProps } from "../General/TabPanel";
import RswState from "../../enums/robot/rsw/rsw-state";
import { ActionType, CommandButtonErrors } from "../../enums/command-hub/command-buttons";
import HttpFetchStatus from "../../enums/api/http-fetch-status";
import RswMaintenance from "../../enums/robot/rsw/rsw-maintenance";
import SignalRConnectionState from "../../enums/api/signalr-connection-state";
import HttpError from "../../interfaces/api/http-error";
import {
  ActionStatus,
  CleaningCommandButtonSender,
  getDefaultCleaningCommandButtonSender,
} from "../../interfaces/robot/buttons/command-buttons";
import RswNavigator from "../../interfaces/robot/navigator";
import Mission from "../../interfaces/robot/missions/mission";
import HardwareMonitoring from "../../interfaces/hardware-monitor/hardware-monitoring";
import SystemSerialNumber from "../../interfaces/general/system-serial-data";
import AuthenticationToken from "../../interfaces/authentication/authentication-token";
import ValidationResult from "../../interfaces/api/validation-result";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RoomData from "../../interfaces/robot/rooms/room-data";
import { AutoFillInNavigator } from "../../enums/command-hub/navigator";

const HomeIcon = withStyles(Home, (theme) => ({
  root: {
    color: theme.palette.info.main,
  },
}));

const AbortIcon = withStyles(StopCircleOutlinedIcon, (theme) => ({
  root: {
    color: theme.palette.secondary.main,
  },
}));

const PauseIcon = withStyles(PauseCircleOutlineIcon, (theme) => ({
  root: {
    color: theme.palette.grey[700],
  },
}));

const ResumeIcon = withStyles(PlayCircleOutlineIcon, (theme) => ({
  root: {
    color: theme.palette.grey[700],
  },
}));

const WakeupIcon = withStyles(AlarmOnIcon, (theme) => ({
  root: {
    color: theme.palette.grey[700],
  },
}));

const ActivateScheduleStyledIcon = withStyles(ActiveScheduleIcon, (theme) => ({
  root: {
    color: theme.palette.info.main,
  },
}));

const ProgressSpinner = withStyles(CircularProgress, (theme) => ({
  root: {
    color: theme.palette.grey[700],
  },
}));

const useStyles = makeStyles()((theme) => ({
  baseButton: {
    boxShadow: theme.shadows[2],
    minWidth: theme.spacing(20),
  },
  navigatorIcon: {
    width: "24px",
    height: "24px",
    marginRight: theme.spacing(1),
  },
}));

export const getCleaningCommandButtonIconFromType = (actionType: ActionType) => {
  switch (actionType) {
    case ActionType.Abort:
      return <AbortIcon />;
    case ActionType.Home:
      return <HomeIcon />;
    case ActionType.Pause:
      return <PauseIcon />;
    case ActionType.Resume:
      return <ResumeIcon />;
    case ActionType.WakeUp:
      return <WakeupIcon />;
    case ActionType.ActivateSchedule:
      return <ActivateScheduleStyledIcon />;
    case ActionType.ConfirmMaintenance:
      return <WarningAmberIcon />;
    default:
      return null;
  }
};

enum CleaningType {
  QuickCleaning,
  MissionCleaning,
  ZoneCleaning,
}


interface DashboardCommandButtonProps {
  currentRobotState: RswState;
  currentRoomId?: string | null;
  actionStatus: ActionStatus;
  awaitingResponse: boolean;
  onCommandButtonClick: (cleaningCommandMsg: CleaningCommandButtonSender) => void;
  availableNavigators: RswNavigator[];
  missions: Mission[];
  availableRooms: RoomData[];
  abortedMissionId: string | null;
  // help robot
  helpRobotParams?: {
    receivedError: CommandButtonErrors | null;
    activeMaintenances?: RswMaintenance[];
    onResolveIssueSuccess: (open: boolean) => void;
    hardwareMonitoringParams: {
      hardwareMonitoringData: HardwareMonitoring[];
      onSetHardwareMonitoringParams: (
        component: HardwareMonitoring,
        extendable: boolean,
        dateOfReplacement?: { value: DateTime; validation: ValidationResult },
        technician?: string,
        rating?: number,
        feedback?: string
      ) => void;
      serialNumberData: SystemSerialNumber | null;
      authTokenData: AuthenticationToken | null;
    };
    rebootRobotParams: {
      rebootData: {
        getStatus: { status: HttpFetchStatus; error: HttpError | null };
        postStatus: { status: HttpFetchStatus; error: HttpError | null };
        isRebootRequired: boolean;
      };
      hostHubConnectionStatus: SignalRConnectionState | null;
      updateTimeoutData: { length: number; startTime: number | null };
      onGetIsRebootRequired: () => void;
      onPostStartReboot: () => void;
      onSetBackendDisconnectionExpectation: (disconnection: boolean) => void;
      onSuccessRebootSnackbarOpen: (open: boolean) => void;
    };
  };
}

const DashboardCommandButton: React.FC<DashboardCommandButtonProps> = ({
  currentRobotState,
  actionStatus,
  awaitingResponse,
  onCommandButtonClick,
  currentRoomId,
  availableNavigators,
  missions,
  abortedMissionId,
  availableRooms,
  helpRobotParams,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const [isLoading, setIsLoading] = useState(false);
  const [isSelectCleanOpen, setIsSelectCleanOpen] = useState(false);
  const [isInteractionDialogOpen, setIsInteractionDialogOpen] = useState(false);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const [hasWarning, setHasWarning] = useState(false);
  const [dialogFrozenRobotState, setDialogFrozenRobotState] = useState<RswState | null>(null); // used for keep the state of maintenance in the dialog while waiting for response
  const [cleaningType, setCleaningType] = useState(CleaningType.QuickCleaning);
  const [missionId, setMissionId] = useState<string | null>(null);
  const [navigatorName, setNavigatorName] = useState<string>(AutoFillInNavigator);
  const [doWallFollow, setDoWallFollow] = useState(true);
  const [repeats, setRepeats] = useState(1);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [fixedOrder, setFixedOrder] = useState(false);

  const actionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const buttonIcon = getCleaningCommandButtonIconFromType(actionStatus.action);
  const isSelectCleanButton = actionStatus.action === ActionType.Clean;
  const isMaintenanceButton = actionStatus.action === ActionType.ConfirmMaintenance;

  useEffect(() => {
    if (!awaitingResponse) {
      setIsLoading(false);
    }
  }, [awaitingResponse]);

  const filteredRooms = availableRooms.filter(
    (r) => r.floorId === availableRooms.find((r) => r.id === currentRoomId)?.floorId && !r.isArchived
  );

  const missionFilter = (mission: Mission) => {
    return filteredRooms.some((r) => r.id === mission.room);
  };

  const missionSort = (a: Mission, b: Mission) => {
    const roomNameA = filteredRooms.find((r) => r.id === a.room)?.name ?? "";
    const roomNameB = filteredRooms.find((r) => r.id === b.room)?.name ?? "";
    if (roomNameA === roomNameB) {
      return a.name > b.name ? 1 : -1;
    }
    return roomNameA > roomNameB ? 1 : -1;
  };

  const sortedMissions = [...missions].sort(missionSort);

  const hasKnownRoom = currentRoomId != null;

  const buttonClickHandler = () => {
    if (isSelectCleanButton) {
      setCleaningType(CleaningType.QuickCleaning);
      setIsSelectCleanOpen(true);
    } else if (actionStatus.action === ActionType.ConfirmMaintenance) {
      setDialogFrozenRobotState(currentRobotState);
      setIsInteractionDialogOpen(true);
    } else {
      sendCommandDispatch();
    }
  };

  const resumeAbortedMissionClickHandler = () => {
    if (abortedMissionId != null) {
      sendCommandDispatch(abortedMissionId);
    }
  };

  const sendCommandDispatch = (overrideMissionId?: string) => {
    const isQuick = cleaningType === CleaningType.QuickCleaning;
    const isMission = cleaningType === CleaningType.MissionCleaning;
    const isZone = cleaningType === CleaningType.ZoneCleaning;

    const cleaningCommandMsg = getDefaultCleaningCommandButtonSender({
      action: actionStatus.action,
      navigatorName: isQuick ? navigatorName : isZone ? "ZoneCleaning" : null,
      missionId: overrideMissionId ?? (isMission ? missionId : null),
      doWallFollow: isQuick ? doWallFollow : false,
      repeat: repeats,
      selectedZoneIds: isZone ? selectedZoneIds : [],
      fixedOrder: isZone ? fixedOrder : false,
    });
    onCommandButtonClick(cleaningCommandMsg);
    setIsLoading(true);
  };

  const closeSelectHandler = () => {
    setIsSelectCleanOpen(false);
  };

  const resetCleaningSelections = () => {
    setMissionId(null);
    setNavigatorName(AutoFillInNavigator);
    setSelectedZoneIds([]);
    setFixedOrder(false);
  };

  const startCleaningClickHandler = () => {
    sendCommandDispatch();
    setIsSelectCleanOpen(false);
    setCleaningType(CleaningType.QuickCleaning);
    resetCleaningSelections();
  };

  const tabsChangeHandler = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setCleaningType(newValue);
    resetCleaningSelections();
  };

  const navigatorChangeHandler = (payload: Partial<NavigatorChangeProps>) => {
    if (payload.navigatorName != null) {
      setNavigatorName(payload.navigatorName);
    }
    if (payload.doWallFollow != null) {
      setDoWallFollow(payload.doWallFollow);
    }
    if (payload.repeats != null) {
      setRepeats(payload.repeats);
    }
  };

  const missionChangeHandler = (newId: string) => {
    setMissionId(newId);
  };

  const closeInteractionDialogHandler = () => {
    setDialogFrozenRobotState(null);
    setIsInteractionDialogOpen(false);
  };

  const confirmInteractionDialogHandler = () => {
    setHasWarning(false);
    setIsWaitingResponse(true);
    sendCommandDispatch();

    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
    }

    actionTimeoutRef.current = setTimeout(() => {
      setIsWaitingResponse(false);

      if (helpRobotParams?.receivedError) {
        setHasWarning(true);
      } else {
        setIsInteractionDialogOpen(false);
        if (helpRobotParams?.onResolveIssueSuccess) {
          helpRobotParams.onResolveIssueSuccess(true);
        }
      }
    }, 10000);
  };

  const getButtonTitle = (): string => {
    if (actionStatus.action === ActionType.ConfirmMaintenance) {
      return t("label-help-robot");
    } else {
      return t("cleaning-command-" + actionStatus.action) ?? "";
    }
  };

  if (!isInteractionDialogOpen && !actionStatus.isEnabled && actionStatus.disableReason == null) {
    return null;
  }

  return (
    <React.Fragment>
      <Tooltip
        title={
          actionStatus.isEnabled || actionStatus.disableReason == null ? undefined : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {t("tooltip-disabled-button-" + actionStatus.disableReason)}
              </Typography>
              <Typography variant="body2">
                {t("tooltip-disabled-button-description-" + actionStatus.disableReason)}
              </Typography>
            </Box>
          )
        }
      >
        <span>
          <Button
            className={classes.baseButton}
            variant={isSelectCleanButton || isMaintenanceButton ? "contained" : "outlined"}
            size="large"
            startIcon={isLoading ? <ProgressSpinner size="22px" /> : buttonIcon}
            onClick={buttonClickHandler}
            disabled={!actionStatus.isEnabled || awaitingResponse}
            color={isMaintenanceButton ? "warning" : "primary"}
            data-testid={getButtonTitle().replace(" ", "-") + "-id"}
            sx={{ fontSize: matchesSmUp ? "medium" : "small" }}
          >
            {getButtonTitle()}
          </Button>
        </span>
      </Tooltip>

      {isSelectCleanButton &&
        abortedMissionId != null &&
        [RswState.Idle, RswState.Ready].includes(currentRobotState) && (
          <Button
            className={classes.baseButton}
            variant="outlined"
            size="large"
            startIcon={isLoading ? <ProgressSpinner size={24} /> : <PlayCircleOutlineIcon />}
            onClick={resumeAbortedMissionClickHandler}
            color={"primary"}
          >
            {t("button-resume-aborted-mission")}
          </Button>
        )}

        {isSelectCleanButton && (
          <DialogWrapperWithClose
            open={isSelectCleanOpen}
            onClose={closeSelectHandler}
            maxWidth="md"
            fullWidth
            title={t("quick-cleaning")}
            PaperProps={{
              sx: {
                maxHeight: { xs: "calc(90dvh - 32px)", sm: "calc(80dvh - 48px)" },
              },
            }}
          >
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                pb: 1,
              }}
            >
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                id="schedule-entry-navigator-select"
                value={cleaningType}
                onChange={tabsChangeHandler}
                sx={{ flexShrink: 0 }}
              >
                <Tab label={t("quick-cleaning")} {...TabPanelA11yProps(CleaningType.QuickCleaning)} />
                <Tooltip title={!hasKnownRoom ? t("robot-not-in-known-room") : ""} placement="top">
                  <Tab
                    label={t("mission-cleaning")}
                    disabled={!hasKnownRoom}
                    sx={!hasKnownRoom ? { pointerEvents: "auto", cursor: "not-allowed" } : undefined}
                    {...TabPanelA11yProps(CleaningType.MissionCleaning)}
                  />
                </Tooltip>
                <Tooltip title={!hasKnownRoom ? t("robot-not-in-known-room") : ""} placement="top">
                  <Tab
                    label={t("zone-cleaning")}
                    disabled={!hasKnownRoom}
                    sx={!hasKnownRoom ? { pointerEvents: "auto", cursor: "not-allowed" } : undefined}
                    {...TabPanelA11yProps(CleaningType.ZoneCleaning)}
                  />
                </Tooltip>
              </Tabs>

              <Box
                key={cleaningType}
                role="tabpanel"
                id={`simple-tabpanel-${cleaningType}`}
                aria-labelledby={`simple-tab-${cleaningType}`}
                sx={{
                  "@keyframes kQuickCleanFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
                  animation: "kQuickCleanFadeIn 0.2s ease-in",
                  pt: 0.5,
                }}
              >
                {cleaningType === CleaningType.QuickCleaning && (
                  <NavigatorSelection
                    navigatorName={navigatorName}
                    navigators={availableNavigators}
                    onChange={navigatorChangeHandler}
                    doWallFollow={doWallFollow}
                  />
                )}
                {cleaningType === CleaningType.MissionCleaning && (
                  <MissionSelect
                    missionId={missionId ?? ""}
                    onMissionChange={missionChangeHandler}
                    missionFilter={missionFilter}
                    missions={sortedMissions}
                    filteredRooms={filteredRooms}
                    currentRoomId={currentRoomId}
                  />
                )}
                {cleaningType === CleaningType.ZoneCleaning && (
                  <Box sx={{ height: 380, mt: 0.5 }}>
                    <QuickCleanZoneTab
                      initialRoomId={currentRoomId}
                      selectedZoneIds={selectedZoneIds}
                      fixedOrder={fixedOrder}
                      onZoneSelectionChanged={(ids, newFixedOrder) => {
                        setSelectedZoneIds(ids);
                        setFixedOrder(newFixedOrder);
                      }}
                    />
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={<PlayCircleOutlineIcon />}
                onClick={startCleaningClickHandler}
                disabled={
                  (cleaningType === CleaningType.MissionCleaning && !missionId) ||
                  (cleaningType === CleaningType.ZoneCleaning && selectedZoneIds.length === 0)
                }
              >
                {t("cleaning-command-start-cleaning")}
              </Button>
            </DialogActions>
          </DialogWrapperWithClose>
        )}

      <HelpRobotDialog
        isOpen={isInteractionDialogOpen && (dialogFrozenRobotState ?? currentRobotState) != RswState.Unknown}
        onClose={closeInteractionDialogHandler}
        onConfirm={confirmInteractionDialogHandler}
        currentRobotState={dialogFrozenRobotState ?? currentRobotState}
        isWaitingResponse={isWaitingResponse}
        hasWarning={hasWarning}
        activeMaintenances={helpRobotParams?.activeMaintenances}
        hardwareMonitoringParams={helpRobotParams?.hardwareMonitoringParams}
        rebootRobotParams={helpRobotParams?.rebootRobotParams}
      />
    </React.Fragment>
  );
};

export default DashboardCommandButton;
